import { createContext, createElement, useContext } from 'react';
import type { FC, ReactNode } from 'react';

import type { DeskActions, DeskData } from '../desk/types';
import type { CrmCreate, CrmCreateRole, PageEntity, PersonRole, RoleChoice, PendingActions, Widget, WidgetPlaceholder } from '../widgets/types';
import type {
  ActiveBotItem,
  CallReading,
  CrmSearchState,
  MeetingParticipant,
  ModernTab,
  PanelBusy,
  ParticipantUpdate,
  RecordingAttendee,
  RecordingSetup,
  SMDModalMode,
  UploadEntry,
} from './types';

/**
 * The seam between the panel's views and whatever feeds them.
 *
 * Views under `packages/ui/src/` read `PanelData`, ask for things through
 * `PanelActions`, and reach the host through `PanelPlatform` — never the store,
 * `chrome.*`, the API or env. The extension fills all three from Rematch and
 * chrome (`src/panel/providers/`); the webapp tour fills them from a scripted
 * world. A container is just:
 *
 *   <PanelDataProvider data={…} actions={…} platform={…}><View/></PanelDataProvider>
 */

export type PanelTab = ModernTab;
export type PanelSheet = Exclude<SMDModalMode, null>;
/** Which rail a card belongs to; its actions post back into the same one. */
export type WidgetSource = 'page' | 'bot' | 'desk';

/** What the panel shows. */
export interface PanelData {
  tab: PanelTab;
  meetings: ActiveBotItem[];
  /**
   * A desktop recording has been asked for and is not in `meetings` yet: the
   * panel says "Starting recording…" and the desktop start stays off.
   */
  desktopStarting: boolean;
  currentBotId: string | null;
  transcript: string;
  botWidgets: Widget[];
  pageWidgets: Widget[];
  /** Between announcing a page and the first response for it. */
  pageLoading: boolean;
  /** The rail is showing a page the recruiter captured (Alt+X), not a CRM record. */
  pageCaptured: boolean;
  /** A captured page's entities, most central first; empty for a CRM page. */
  pageEntities: PageEntity[];
  /** The entity the captured page's cards are about, when the server named one. */
  pageFocusEntityId: string | null;
  /**
   * The "+" beside attach: the focus person is known not to be in the CRM, so
   * the panel offers to create them. Null (no button) unless the server said
   * `status: "none"` for the entity in focus and `crm_create` is not hidden
   * from this person. `role` null means the recruiter must choose one.
   */
  crmCreate: CrmCreate | null;
  /** The focus person's role can be settled from the panel (null: no control). */
  roleChoice: RoleChoice | null;
  /** The server is still reading the captured page; its cards are placeholders. */
  pageReading: boolean;
  /**
   * The page in front of the recruiter could be read with the capture
   * shortcut, and the panel says so at its top; null when it could not (a CRM
   * page, Dooglie's own, the page on the rail) or capture is not theirs yet.
   * `reading` while a press on it is out; `shortcut` is the bound keys as the
   * browser writes them ("Alt+X", "⌥X"), "" when none are bound.
   */
  pageNudge: { reading: boolean; shortcut: string } | null;
  /** The actions each rail's cards are waiting on, keyed by widget key. */
  pending: { page: PendingActions; bot: PendingActions; desk?: PendingActions };
  /**
   * The Desk tab: the recruiter's goals and open threads. Undefined when the
   * Desk is not this person's yet (a prerelease feature), and the tab is then
   * not drawn.
   */
  desk?: DeskData;
  runProgress: { active: boolean; ratio_done: number };
  uploads: UploadEntry[];
  sheet: PanelSheet | null;
  recordingSetup: RecordingSetup | null;
  call: CallReading | null;
  snackbar: string | null;
  /** What is in flight that a control shows as busy. */
  busy: PanelBusy;
  /** The site of the page the Insights rail is showing ("app.loxo.co"), which
   *  the recording sheet names its page's people by; null when there is none. */
  pageSite: string | null;
  /** The recording sheet's CRM people search. */
  crmSearch: CrmSearchState;
  /** The current meeting's participants, for the Attendees sheet. */
  participants: MeetingParticipant[];
}

/** One card action, as the card's own controls submit it. */
export interface RunWidgetActionArgs {
  inputs: string[];
  widgetId: string;
  actionId: string;
  payload?: Record<string, unknown>;
  /** Defaults to the bot rail, where the panic/ask chrome lives. */
  source?: WidgetSource;
  /** What the rail shows until the server has a card, for an action that creates one. */
  placeholder?: WidgetPlaceholder;
}

/** What a create-in-CRM request came to, as far as the button must act on it. */
export type CreateInCrmOutcome = 'done' | 'role_required';

/** What the panel can ask for. */
export interface PanelActions {
  setTab(tab: PanelTab): void;
  openSheet(sheet: PanelSheet): void;
  closeSheet(): void;
  createMeetingBot(args: { meetingUrl: string; attendees: RecordingAttendee[] }): Promise<void>;
  /** Start a desktop capture of the call (no participant joins). */
  startCapture(args: { attendees: RecordingAttendee[] }): Promise<void>;
  stopRecording(): Promise<void>;
  pressPanic(): Promise<void>;
  ask(question: string): Promise<void>;
  runWidgetAction(args: RunWidgetActionArgs): Promise<void>;
  /** Create the focus person in the CRM (a bare record; Dooglie then writes
   *  their notes and fields). `role` only when `crmCreate.role` is null.
   *  Answers `role_required` when the server wants the recruiter to choose
   *  (the panel then opens its role menu); the outcome is otherwise told by
   *  the snackbar. */
  createInCrm(args: { entityId: string; role?: CrmCreateRole }): Promise<CreateInCrmOutcome>;
  /** Settle the focus person's role ("Neither" included); the page's cards
   *  follow. Failures are told by the snackbar. */
  setPersonRole(args: { entityId: string; role: PersonRole }): Promise<void>;
  /** Show the captured page's cards for another of its entities. */
  focusEntity(entityId: string): void;
  /** Open the browser's page for binding the capture shortcut. */
  openShortcutSettings(): void;
  selectMeeting(botId: string): void;
  uploadFiles(files: File[]): Promise<void>;
  searchCrmPeople(query: string): Promise<void>;
  /** Ask the host for permission to act on Google Meet; true when held. */
  requestMeetAccess(): Promise<boolean>;
  /** Move to another meeting that is live, ending the current one's view. */
  joinMeeting(meeting: ActiveBotItem): Promise<void>;
  /** Save the Attendees sheet's edits; true when they were saved. */
  saveParticipants(participants: ParticipantUpdate[]): Promise<boolean>;
  /** The Desk tab's goal controls; undefined where the Desk is not offered. */
  desk?: DeskActions;
}

/** What the host page provides. */
export interface PanelPlatform {
  /** Where modals portal to. Unset: `document.body` (the side panel). */
  modalContainer?: HTMLElement;
  /**
   * The panel is a part of a page that goes on working beside it (the tour's
   * stage). Its modals then neither trap focus nor lock the page's scroll.
   * Unset (the extension): a modal is modal, even inside `PanelFrame`.
   */
  embedded?: boolean;
  webappUrl: string;
  /** Open a link the user clicked (a new tab in the extension). */
  openLink(url: string): void;
  /**
   * Small per-device view choices (where the transcript sits). Unset (the
   * tour): nothing is remembered and every view opens on its default.
   */
  preferences?: PanelPreferences;
}

/** A per-device key/value store for view choices. Never throws. */
export interface PanelPreferences {
  read(key: string): string | null;
  write(key: string, value: string): void;
}

const PanelPlatformContext = createContext<PanelPlatform | null>(null);

/**
 * Supplies only the platform half, over whatever is above — how a panel frame
 * inside a page points modals at itself (`PanelFrame`).
 */
export const PanelPlatformProvider: FC<{ platform: PanelPlatform; children?: ReactNode }> = ({
  platform,
  children,
}) => createElement(PanelPlatformContext.Provider, { value: platform }, children);

const PanelDataContext = createContext<PanelData | null>(null);
const PanelActionsContext = createContext<PanelActions | null>(null);

export interface PanelDataProviderProps {
  data: PanelData;
  actions: PanelActions;
  platform: PanelPlatform;
  children?: ReactNode;
}

/** Supplies the three halves of the seam to every panel view below it. */
export const PanelDataProvider: FC<PanelDataProviderProps> = ({
  data,
  actions,
  platform,
  children,
}) =>
  createElement(
    PanelPlatformContext.Provider,
    { value: platform },
    createElement(
      PanelActionsContext.Provider,
      { value: actions },
      createElement(PanelDataContext.Provider, { value: data }, children),
    ),
  );

const required = <T,>(value: T | null, hook: string): T => {
  if (value === null) {
    throw new Error(`${hook}() needs a <PanelDataProvider> above it`);
  }
  return value;
};

export const usePanelData = (): PanelData =>
  required(useContext(PanelDataContext), 'usePanelData');

export const usePanelActions = (): PanelActions =>
  required(useContext(PanelActionsContext), 'usePanelActions');

export const usePanelPlatform = (): PanelPlatform =>
  required(useContext(PanelPlatformContext), 'usePanelPlatform');
